import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface AppointmentBookingProps {
  propertyId: string;
  propertyTitle: string;
  onClose: () => void;
}

export default function AppointmentBooking({ propertyId, propertyTitle, onClose }: AppointmentBookingProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Generar fechas disponibles (próximos 30 días, excluyendo domingos)
  const generateAvailableDates = () => {
    const dates = [];
    const today = new Date();
    
    for (let i = 1; i <= 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      
      // Excluir domingos (0 = domingo)
      if (date.getDay() !== 0) {
        dates.push({
          value: date.toISOString().split('T')[0],
          label: date.toLocaleDateString('es-ES', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })
        });
      }
    }
    
    return dates;
  };

  // Horarios disponibles
  const availableTimes = [
    { value: '09:00', label: '9:00 AM' },
    { value: '10:00', label: '10:00 AM' },
    { value: '11:00', label: '11:00 AM' },
    { value: '12:00', label: '12:00 PM' },
    { value: '14:00', label: '2:00 PM' },
    { value: '15:00', label: '3:00 PM' },
    { value: '16:00', label: '4:00 PM' },
    { value: '17:00', label: '5:00 PM' },
    { value: '18:00', label: '6:00 PM' }
  ];

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Simular envío de cita (aquí integrarías con tu API)
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setShowSuccess(true);
      
      // Cerrar después de 3 segundos
      setTimeout(() => {
        onClose();
      }, 3000);
      
    } catch (error) {
      console.error('Error al agendar cita:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = formData.name && formData.email && formData.phone && formData.date && formData.time;

  if (showSuccess) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-check-line text-3xl text-green-600"></i>
          </div>
          <h3 className="text-2xl font-semibold text-gray-800 mb-2">{t('appointmentConfirmed')}</h3>
          <p className="text-gray-600 mb-4">{t('appointmentConfirmedMessage')}</p>
          <div className="bg-gray-50 rounded-lg p-4 text-left">
            <h4 className="font-medium text-gray-800 mb-2">{t('appointmentDetails')}</h4>
            <div className="space-y-2 text-sm text-gray-600">
              <p><strong>{t('property')}:</strong> {propertyTitle}</p>
              <p><strong>{t('name')}:</strong> {formData.name}</p>
              <p><strong>{t('date')}:</strong> {formData.date}</p>
              <p><strong>{t('time')}:</strong> {formData.time}</p>
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-4">{t('closingAutomatically')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <i className="ri-calendar-line text-2xl text-blue-600"></i>
            <div>
              <h2 className="text-xl font-bold text-gray-800">{t('scheduleVisit')}</h2>
              <p className="text-gray-600 text-sm">{propertyTitle}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl text-gray-600"></i>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Información de contacto */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('contactInformation')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('fullName')} *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('enterFullName')}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('email')} *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('enterEmail')}
                    required
                  />
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('phone')} *
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t('enterPhone')}
                  required
                />
              </div>
            </div>

            {/* Fecha y hora */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('selectDateTime')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('preferredDate')} *
                  </label>
                  <select
                    value={formData.date}
                    onChange={(e) => handleInputChange('date', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">{t('selectDate')}</option>
                    {generateAvailableDates().map(date => (
                      <option key={date.value} value={date.value}>
                        {date.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('preferredTime')} *
                  </label>
                  <select
                    value={formData.time}
                    onChange={(e) => handleInputChange('time', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">{t('selectTime')}</option>
                    {availableTimes.map(time => (
                      <option key={time.value} value={time.value}>
                        {time.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Mensaje adicional */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('additionalMessage')} ({t('optional')})
              </label>
              <textarea
                value={formData.message}
                onChange={(e) => handleInputChange('message', e.target.value)}
                rows={4}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder={t('enterAdditionalMessage')}
                maxLength={500}
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.message.length}/500 {t('characters')}
              </p>
            </div>

            {/* Información importante */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex gap-3">
                <i className="ri-information-line text-blue-600 text-lg mt-0.5"></i>
                <div className="text-sm">
                  <h4 className="font-medium text-blue-800 mb-2">{t('importantInformation')}</h4>
                  <ul className="space-y-1 text-blue-700">
                    <li>• {t('visitDuration')}</li>
                    <li>• {t('confirmationMessage')}</li>
                    <li>• {t('cancellationPolicy')}</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Botones */}
            <div className="flex gap-4 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                {t('cancel')}
              </button>
              <button
                type="submit"
                disabled={!isFormValid || isSubmitting}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    {t('scheduling')}
                  </>
                ) : (
                  <>
                    <i className="ri-calendar-check-line"></i>
                    {t('scheduleVisit')}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Alternativa con Readdy Agent */}
        <div className="border-t border-gray-200 p-6 bg-gray-50">
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-3">{t('preferVoiceAssistance')}</p>
            <button
              onClick={() => {
                // Activar Readdy Agent
                const readyButton = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
                if (readyButton) {
                  readyButton.click();
                }
                onClose();
              }}
              className="inline-flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mic-line"></i>
              {t('talkWithAgent')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}